import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import CartTableHeader from "../../../Cart/components/CartTableHeader";
import useMyAccount from "../../../../hooks/useMyAccount";
import { hasImage } from "../../../Products/components/ProductItem";
import { formatCurrency } from "../../../../util/formatters";
import { buildWhatsAppMessageOrderInquiry, getOrderWhatsAppLink } from "../../../../util/whatsapp";
import { useHttp } from "../../../../hooks/useHttp";
import { API_URLS, DEFAULT_HEADERS } from "../../../../util/constants";
import { handleApiError } from "../../../../util/errorHandler";
import { useAuth } from "../../../../hooks/useAuth";

export default function DetailsOrderHistory() {

  const { orderDetail, contactTerra, userData, companyData } = useMyAccount();
  const { sendRequest } = useHttp();
  const { userId } = useAuth();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const orderDate = orderDetail?.order_date;

  // Busca os métodos de pagamento da API (com product_ids do pedido para incluir métodos promocionais)
  useEffect(() => {
    const getPaymentMethods = async () => {
      try {
        const productIds = (orderDetail?.products_data || []).map((p) => p.product_id).filter(Boolean);
        const responseData = await sendRequest(
          API_URLS.PAYMENT_METHODS,
          "POST",
          JSON.stringify({ product_ids: productIds }),
          DEFAULT_HEADERS.JSON
        );
        if (responseData.success) {
          setPaymentMethods(responseData.payment_methods || []);
        }
      } catch (error) {
        handleApiError(error, { context: "DetailsOrderHistory.getPaymentMethods", showToast: false });
      }
    };
    if (orderDetail) getPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderDetail]);

  // Função para obter o link do WhatsApp do vendedor responsável
  const getWhatsAppLink = () => {
    const orderNumber = orderDetail?.order_number || orderDetail?.cart_number || orderDetail?.id_order || "";
    const companyName =
      companyData?.company_name ?? userData?.company_name ?? orderDetail?.customer_data?.company_name ?? "";
    const message = buildWhatsAppMessageOrderInquiry({
      companyName,
      orderNumber: String(orderNumber),
    });
    return getOrderWhatsAppLink(orderDetail, contactTerra, message);
  };

  const sharePdfHandler = async () => {
    if (!userId || !orderDetail) return;
    const idOrder =
      orderDetail?.id_order ?? orderDetail?.order_number ?? orderDetail?.cart_number ?? null;
    if (!idOrder) return;

    setIsGeneratingPdf(true);
    try {
      const response = await fetch(API_URLS.GENERATE_PDF, {
        method: "POST",
        headers: DEFAULT_HEADERS.JSON_WITH_CORS,
        body: JSON.stringify({
          id_user: userId,
          id_order: idOrder,
        }),
      });

      if (!response.ok) {
        // tenta ler JSON de erro, caso exista
        let payload = null;
        try {
          payload = await response.json();
        } catch {
          // ignore
        }
        const err = new Error(
          payload?.message || payload?.error || "Falha ao gerar PDF"
        );
        err.status = response.status;
        err.response = payload;
        throw err;
      }

      const contentType = response.headers.get("content-type") || "";
      if (!contentType.includes("application/pdf")) {
        // backend pode devolver JSON em caso de erro
        let payload = null;
        try {
          payload = await response.json();
        } catch {
          // ignore
        }
        const err = new Error(
          payload?.message ||
          payload?.error ||
          "Resposta inesperada ao gerar PDF"
        );
        err.status = response.status;
        err.response = payload;
        throw err;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // libera depois de um tempo para evitar invalidar antes da aba carregar
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      handleApiError(error, {
        context: "DetailsOrderHistory.sharePdfHandler",
        showToast: false,
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div>
      <div className="container">
        <Link to="/minha-conta/historico-pedidos" title="Voltar ao histórico de pedidos">
          <button
            style={{
              width: "max-content",
              marginBottom: "48px",
              borderRadius: 0,
            }}
            className="theme-btn-1 btn btn-block "
            type="button"
            title="Voltar ao histórico de pedidos"
          >
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
        </Link>
        <div className="row">
          <div className="col-12">
            <div className=" ltn__primary-color title-2">
              <div
                className="mb-10 go-top"
                style={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <h3 className="ltn__primary-color mb-0">
                  Pedido Número {orderDetail?.order_number}
                </h3>
                {orderDetail?.status_order_name && (
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "6px 10px",
                      borderRadius: "999px",
                      backgroundColor:
                        orderDetail?.status_order_color ||
                        "var(--ltn__secondary-color, #dc3545)",
                      color: "#fff",
                      fontSize: "0.85rem",
                      fontWeight: 700,
                      lineHeight: 1,
                    }}
                    aria-label={`Status do pedido: ${orderDetail.status_order_name}`}
                    title={`Status: ${orderDetail.status_order_name}`}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: "8px",
                        height: "8px",
                        borderRadius: "50%",
                        backgroundColor: "rgba(255,255,255,0.9)",
                      }}
                    />
                    {orderDetail.status_order_name}
                  </span>
                )}
              </div>
              <p className="text-muted mb-0" style={{ fontSize: "14px" }}>
                Data do pedido: <strong>{orderDate}</strong>
              </p>
            </div>
          </div>
          <div className="col-lg-12">
            <div className="ltn__checkout-inner">
              <div className="ltn__checkout-single-content ltn__returning-customer-wrap">
                <div id="ltn__returning-customer-login"></div>
              </div>
              <div className="ltn__checkout-single-content">
                <div>
                  <form>
                    <h6>Informações pessoais</h6>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        {/* <h6>Responsável</h6> */}
                        <div className="input-item input-item-lock ltn__custom-icon">
                          <input
                            type="text"
                            name="ltn__name"
                            placeholder={orderDetail?.customer_data?.user_name}
                            disabled
                          />
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        {/* <h6>Empresa</h6> */}
                        <div className="input-item input-item-lock ltn__custom-icon">
                          <input
                            type="text"
                            name="ltn__company"
                            placeholder={
                              orderDetail?.customer_data.company_name
                            }
                            disabled
                          />
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        {/* <h6>Whatsapp</h6> */}
                        <div className="input-item input-item-lock ltn__custom-icon">
                          <input
                            type="email"
                            name="ltn__phone"
                            placeholder={
                              orderDetail?.customer_data.user_cellphone
                            }
                            disabled
                          />
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        {/* <h6>Telefone</h6> */}
                        <div className="input-item input-item-lock ltn__custom-icon">
                          <input
                            type="text"
                            name="ltn__phone"
                            placeholder={
                              orderDetail?.customer_data.user_cellphone
                            }
                            disabled
                          />
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
                <div>
                  <form>
                    <h6>Endereço de entrega</h6>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <div className="input-item input-item-lock ltn__custom-icon">
                          <input
                            type="text"
                            name="ltn__name"
                            placeholder={
                              orderDetail?.customer_data.delivery_address
                                .address
                            }
                            disabled
                          />
                        </div>
                      </div>
                      <div className="col-md-6 mb-3">
                        <div className="input-item input-item-lock ltn__custom-icon">
                          <input
                            type="text"
                            name="ltn__company"
                            placeholder={
                              orderDetail?.customer_data.delivery_address
                                .complement
                            }
                            disabled
                          />
                        </div>
                      </div>
                      <div className="col-md-4 mb-3">
                        <h6>Estado</h6>
                        <div className="input-item input-item-lock ltn__custom-icon">
                          <input
                            type="text"
                            name="ltn__phone"
                            placeholder={
                              orderDetail?.customer_data.delivery_address.state
                            }
                            disabled
                          />
                        </div>
                      </div>
                      <div className="col-md-4 mb-3">
                        <h6>Cidade</h6>
                        <div className="input-item input-item-lock ltn__custom-icon">
                          <input
                            type="text"
                            name="ltn__email"
                            placeholder={
                              orderDetail?.customer_data.delivery_address.city
                            }
                            disabled
                          />
                        </div>
                      </div>
                      <div className="col-md-4 mb-3">
                        <h6>CEP</h6>
                        <div className="input-item input-item-lock ltn__custom-icon">
                          <input
                            type="text"
                            name="ltn__phone"
                            placeholder={
                              orderDetail?.customer_data.delivery_address
                                .zip_code
                            }
                            disabled
                          />
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
        <h2 className=" ltn__primary-color title-2">Itens selecionados</h2>
        <table className="shoping-cart-table table-responsive justify-content-center">
          <CartTableHeader />
          <tbody className="text-align align-center">
            {!orderDetail ? (
              <tr>
                <td className="px-5 cart-product-image text-center">
                  <h1
                    style={{
                      textAlign: "center",
                      color: "red",
                    }}
                  >
                    Nenhum item nesse pedido
                  </h1>
                </td>
              </tr>
            ) : (
              (orderDetail?.products_data || []).map((product, index) => (
                <tr key={index}>
                  <td className="px-5 cart-product-image text-center">
                    <Link
                      to={product.product_id ? `/produto/${product.product_id}` : "/produto"}
                      title={product.product_name ? `Ver produto — ${product.product_name}` : "Ver produto"}
                    >
                      <img src={hasImage(product.image)} alt={`${product.product_name}`} />
                    </Link>
                  </td>
                  <td className="px-5 cart-product-info">
                    <h4 className="ltn__secondary-color">
                      <Link
                        to={'/produto/' + product.product_id}
                        title={`Ver produto — ${product.product_name ?? ""}`}
                      >
                        {product.product_name}
                      </Link>
                    </h4>
                  </td>
                  <td className="px-5 cart-product-price text-center">
                    {formatCurrency(product.product_price)}
                  </td>
                  <td className="px-5 cart-product-subtotal ">
                    <div className="text-center row h-100 align-content-center ">
                      <div>
                        <input
                          type="number"
                          className="cart-plus-minus-box"
                          defaultValue={product.product_quantity}
                          disabled
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-5 cart-product-total text-center">
                    {formatCurrency(product.product_total_price)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <div className="my-5">
          <h4 className="title-2" style={{ color: "#000" }}>Método de pagamento</h4>
        </div>
        <div className="ltn__checkout-payment-method my-5 row">
          {paymentMethods
            .filter((method) => orderDetail?.payment_method === method.payment_name)
            .map((method) => (
              <div key={method.id} className="col-12 text-center">
                <button
                  className="theme-btn-1 btn my-1"
                  disabled
                  type="button"
                  title={method.payment_name ? `Forma de pagamento: ${method.payment_name}` : "Forma de pagamento"}
                  style={{
                    backgroundColor: method.color || "#28a745",
                    borderColor: method.color || "#28a745",
                    color: method.text_color || "#fff",
                    ...(method.style || {}),
                  }}
                >
                  {method.payment_name}
                </button>
              </div>
            ))}
          {paymentMethods.filter((m) => orderDetail?.payment_method === m.payment_name).length === 0 &&
            orderDetail?.payment_method && (
              <div className="col-12 text-center">
                <span className="text-muted">{orderDetail.payment_method}</span>
              </div>
            )}
        </div>
        <div
          className="row justify-content-end mb-5"
          style={{ padding: "16px" }}
        >
          <div className="shoping-cart-total mt-50 justify-content-end">
            <h4 className="title-2" style={{ color: "#000" }}>Total do Pedido</h4>
            <table className="table">
              <tbody>
                <tr>
                  <td>Subtotal</td>
                  <td>{orderDetail?.subtotal_price}</td>
                </tr>
                <tr>
                  <td>Frete</td>
                  <td>{orderDetail?.shipping_price}</td>
                </tr>
                <tr>
                  <td>Desconto</td>
                  <td style={{
                    color: 'green'
                  }}>
                    <strong>
                      -{orderDetail?.discount_price}
                    </strong>
                  </td>
                </tr>
                <tr>
                  <td>Cashback usado</td>
                  <td style={{ color: "var(--ltn__secondary-color, #dc3545)" }}>
                    <strong>{orderDetail?.cashback_used ?? "—"}</strong>
                  </td>
                </tr>
                <tr>
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td>
                    <strong>{orderDetail?.total_price}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
            <div>
              <p className="mt-3">
                Nesse pedido você ganhou{" "}
                <strong style={{ color: "green" }}>
                  {orderDetail?.cashback_earned ?? orderDetail?.discount_cashback ?? "—"}
                </strong>{" "}
                de cashback.
              </p>
            </div>
            <div className="row mt-1">
              <div className="col-8">
                <button
                  className="theme-btn-1 btn my-1 w-100"
                  style={{ backgroundColor: "#49A2D4", }}
                  type="button"
                  onClick={sharePdfHandler}
                  disabled={isGeneratingPdf}
                  title={isGeneratingPdf ? "Gerando PDF..." : "Compartilhar PDF"}
                >
                  <i className="fa-solid fa-lg fa-file-pdf"></i>
                  {isGeneratingPdf ? "Gerando PDF..." : "Compartilhar PDF"}
                </button>
              </div>
              <div className="col-4">
                <a
                  href={getWhatsAppLink()}
                  target="_blank"
                  rel="noreferrer"
                  className="btn my-1 w-100"
                  title="Contatar sobre pedido"
                  aria-label="Contatar sobre pedido"
                  style={{
                    backgroundColor: "#25D366",
                    color: "#FFF",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textDecoration: "none",
                    height: '100%'
                  }}
                >
                  <i className="fa-brands fa-lg fa-whatsapp"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
